import { AppButton, FlexContainer } from '@/ui';
import { ProgressBar } from 'primereact/progressbar';
import { ProgressSpinner } from 'primereact/progressspinner';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

const ProcessingContentSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 36px;
  align-items: center;
  padding-top: 36px;
  width: 100%;
`;

const SpinnerContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 13px;
  align-items: center;
  width: 100%;
`;

const StyledProgressSpinner = styled(ProgressSpinner)`
  width: 136px;
  height: 136px;
  position: relative;

  svg {
    z-index: 1;

    .p-progress-spinner-circle {
      stroke: var(--red-main) !important;
    }
  }

  &:before,
  &:after {
    content: '';
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    border-radius: 50%;
    padding: 0;
    z-index: 0;
  }

  &:before {
    width: 92%;
    height: 92%;
    background-color: var(--gray-dark);
  }

  &:after {
    width: 68%;
    height: 68%;
    background-color: var(--beige-main);
  }
`;

const ProcessingTitle = styled.h2`
  font-family: var(--font-family);
  font-weight: 700;
  font-size: 24px;
  line-height: 1.4;
  color: var(--black-main);
  margin: 0;
  text-align: center;
`;

const ProcessingDescription = styled.p`
  font-family: var(--font-family);
  font-weight: 400;
  font-size: 16px;
  line-height: 1.4;
  color: var(--black-main);
  margin: 0;
  text-align: center;
`;

const ProgressContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  align-items: center;
  justify-content: center;
  width: 542px;
  max-width: 100%;
`;

const ProgressBarWrapper = styled.div`
  position: relative;
  height: 32px;
  width: 387px;
  max-width: 100%;
`;

const StyledProgressBar = styled(ProgressBar)`
  height: 6px;
  width: 100%;
  border-radius: 100px;
  background: var(--gray-dark);
  position: relative;

  .p-progressbar-value {
    background: var(--red-main);
    border-radius: 100px;
  }
`;

const ProgressPercentage = styled.div<{ progress: number }>`
  position: absolute;
  top: 50%;
  left: ${({ progress }) => progress}px;
  transform: translateY(-35%);
  font-family: var(--font-family);
  font-weight: 600;
  font-size: var(--small-font-size);
  line-height: 1.5;
  color: var(--black-main);
  text-align: center;
  pointer-events: none;
`;

export type CourseProcessingProps = {
  title: string;
  description: string;
  onContinue?: () => void;
};

export const CourseProcessing: React.FC<CourseProcessingProps> = ({
  title,
  description,
  onContinue,
}) => {
  const { t } = useTranslation();
  const [progress, setProgress] = useState(0);
  const [startTime] = useState(() => Date.now());
  const wrapperRef = React.useRef<HTMLDivElement | null>(null);
  const percentageRef = React.useRef<HTMLDivElement | null>(null);
  const [leftPx, setLeftPx] = useState(0);

  // Update progress based on elapsed time with logarithmic curve
  // This ensures progress slows down as it approaches 95%, preventing
  // it from getting stuck at 95% for long-running requests
  useEffect(() => {
    const interval = setInterval(() => {
      const elapsedSeconds = (Date.now() - startTime) / 1000;

      // Use a logarithmic curve that reaches ~95% at around 70 seconds
      // Formula: 95 * (1 - e^(-elapsedSeconds / 30))
      // This creates a curve that starts fast and slows down significantly
      const calculatedProgress = 95 * (1 - Math.exp(-elapsedSeconds / 30));

      setProgress(Math.min(calculatedProgress, 95));
    }, 200);

    return () => clearInterval(interval);
  }, [startTime]);

  // compute pixel position for percentage label so it centers on the progress end
  useEffect(() => {
    const updatePosition = () => {
      const wrapper = wrapperRef.current;
      const label = percentageRef.current;
      if (!wrapper || !label) return;

      const labelWidth = label.clientWidth;

      // measure the filled bar element if possible for exact alignment
      const filledEl = wrapper.querySelector(
        '.p-progressbar-value',
      ) as HTMLElement | null;

      const measureAndSet = () => {
        const wrapperWidth = wrapper.clientWidth;
        const filledWidth = filledEl
          ? filledEl.getBoundingClientRect().width
          : (Math.max(0, Math.min(progress, 100)) / 100) * wrapperWidth;

        const x = filledWidth - labelWidth / 2;
        const bounded = Math.max(0, Math.min(x, wrapperWidth - labelWidth));
        setLeftPx(bounded);
      };

      // ensure measurement happens after paint so we read the current layout
      requestAnimationFrame(measureAndSet);

      // update on resize to keep alignment
      window.addEventListener('resize', measureAndSet);

      return () => {
        window.removeEventListener('resize', measureAndSet);
      };
    };

    // call and subscribe
    const cleanup = updatePosition();
    return cleanup;
  }, [progress]);

  return (
    <ProcessingContentSection>
      <SpinnerContainer>
        <StyledProgressSpinner strokeWidth="6" animationDuration="1.5s" />
        <FlexContainer gap={10} direction="column">
          <ProcessingTitle>{title}</ProcessingTitle>
          <ProcessingDescription>{description}</ProcessingDescription>
        </FlexContainer>
      </SpinnerContainer>

      <ProgressContainer>
        <ProgressBarWrapper ref={wrapperRef}>
          <StyledProgressBar value={progress} showValue={false} />
          <ProgressPercentage ref={percentageRef} progress={leftPx}>
            {Math.round(progress)}%
          </ProgressPercentage>
        </ProgressBarWrapper>
        {onContinue && (
          <AppButton onClick={onContinue}>{t('button.continue')}</AppButton>
        )}
      </ProgressContainer>
    </ProcessingContentSection>
  );
};
